import React, { useState, useCallback } from "react";
import {
  Form,
  Input,
  DatePicker,
  Button,
  Typography,
  Alert,
  Spin,
  Steps,
  message,
} from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  TeamOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  getPublicAvailability,
  createPublicReservation,
  type AvailabilitySlot,
} from "../../utils/api/reservationsAPI";
import "./PublicReservation.scss";
import VoicePublicReservation from "./VoicePublicReservation";
import "./VoicePublicReservation.scss";

const { Title, Text, Paragraph } = Typography;

type Step = "datetime" | "details" | "success";

const PublicReservation: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get("clientId") || "";
  const restaurantName = params.get("name") || "Restaurant";

  const [form] = Form.useForm();
  const [step, setStep] = useState<Step>("datetime");

  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [guestCount, setGuestCount] = useState(2);

  const [submitting, setSubmitting] = useState(false);
  const [confirmationData, setConfirmationData] = useState<{
    status: string;
    message: string;
  } | null>(null);

  const fetchSlots = useCallback(
    async (date: dayjs.Dayjs) => {
      if (!clientId) return;
      setLoadingSlots(true);
      setSlotsError(null);
      setSlots([]);
      setSelectedTime(null);
      try {
        const res = await getPublicAvailability(clientId, date.format("YYYY-MM-DD"));
        if (!res.data.available) {
          const reasons: Record<string, string> = {
            closed: "The restaurant is closed on this day.",
            past_date: "You cannot book for a past date.",
            too_far_in_advance: "This date is too far in advance.",
          };
          setSlotsError(reasons[res.data.reason || ""] || "Bookings for this date are not available.");
        } else {
          setSlots(res.data.slots || []);
        }
      } catch {
        setSlotsError("Could not load availability. Please try again.");
      } finally {
        setLoadingSlots(false);
      }
    },
    [clientId]
  );

  const onDateChange = (date: dayjs.Dayjs | null) => {
    setSelectedDate(date);
    if (date) fetchSlots(date);
  };

  const handleSubmit = async (values: any) => {
    if (!clientId || !selectedDate || !selectedTime) return;
    setSubmitting(true);
    try {
      const res = await createPublicReservation({
        clientId,
        guestName: values.guestName.trim(),
        guestPhone: values.guestPhone.trim(),
        guestEmail: values.guestEmail?.trim() || undefined,
        date: selectedDate.format("YYYY-MM-DD"),
        time: selectedTime,
        numberOfGuests: guestCount,
        note: values.note?.trim() || undefined,
        source: "website",
      });
      setConfirmationData({ status: res.data.status, message: res.data.message });
      setStep("success");
    } catch (err: any) {
      message.error(err?.response?.data?.error || "Booking failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setStep("datetime");
    setSelectedDate(null);
    setSelectedTime(null);
    setSlots([]);
    setGuestCount(2);
    setConfirmationData(null);
  };

  if (!clientId) {
    return (
      <div className="pr-wrap pr-wrap--error">
        <Alert type="error" message="Invalid booking link. Please contact the restaurant." />
      </div>
    );
  }

  // Split slots — hide past slots entirely, only show genuinely full ones
  const availableSlots = slots.filter((s) => s.available);
  const fullSlots = slots.filter((s) => !s.available && !s.isPast);

  return (
    <div className="pr-wrap">
      {/* ── Header ── */}
      <div className="pr-header">
        <div className="pr-header__emoji">🍽️</div>
        <h1 className="pr-header__name">{restaurantName}</h1>
        <p className="pr-header__sub">Online table reservation</p>
      </div>

      {/* ── Steps indicator ── */}
      {step !== "success" && (
        <div className="pr-steps">
          <div className={`pr-step ${step === "datetime" ? "pr-step--active" : "pr-step--done"}`}>
            <div className="pr-step__circle">
              {step === "details" ? <CheckCircleOutlined /> : "1"}
            </div>
            <span>Date & Time</span>
          </div>
          <div className="pr-step__line" />
          <div className={`pr-step ${step === "details" ? "pr-step--active" : ""}`}>
            <div className="pr-step__circle">2</div>
            <span>Your Details</span>
          </div>
        </div>
      )}

      {/* ── Step 1: Date & Time ── */}
      {step === "datetime" && (
        <div className="pr-body">
          {/* Date picker */}
          <div className="pr-field-block">
            <label className="pr-label">
              <CalendarOutlined className="pr-label__icon" /> Select a Date
            </label>
            <DatePicker
              value={selectedDate}
              onChange={onDateChange}
              format="dddd, DD MMMM YYYY"
              inputReadOnly
              style={{ width: "100%" }}
              className="pr-datepicker"
              disabledDate={(d) =>
                d.isBefore(dayjs(), "day") || d.isAfter(dayjs().add(60, "day"), "day")
              }
              allowClear={false}
              size="large"
            />
          </div>

          {/* Time slots */}
          {selectedDate && (
            <div className="pr-field-block">
              <label className="pr-label">
                <ClockCircleOutlined className="pr-label__icon" /> Select a Time
              </label>

              {loadingSlots && (
                <div className="pr-loading">
                  <Spin size="small" />
                  <span>Loading availability...</span>
                </div>
              )}

              {slotsError && (
                <Alert type="warning" message={slotsError} className="pr-alert" showIcon />
              )}

              {!loadingSlots && !slotsError && slots.length > 0 && (
                <>
                  {availableSlots.length > 0 && (
                    <div className="pr-slots">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot.time}
                          type="button"
                          className={`pr-slot${slot.time === selectedTime ? " pr-slot--selected" : ""}`}
                          onClick={() => setSelectedTime(slot.time)}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  )}

                  {fullSlots.length > 0 && (
                    <>
                      {availableSlots.length > 0 && (
                        <p className="pr-slots-divider">Full slots</p>
                      )}
                      <div className="pr-slots pr-slots--full">
                        {fullSlots.map((slot) => (
                          <button
                            key={slot.time}
                            type="button"
                            className="pr-slot pr-slot--full"
                            disabled
                          >
                            {slot.time}
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  {availableSlots.length === 0 && !slotsError && (
                    <Alert
                      type="info"
                      message="No available slots for this date. Please choose another day."
                      showIcon
                      className="pr-alert"
                    />
                  )}
                </>
              )}
            </div>
          )}

          <Button
            type="primary"
            size="large"
            block
            disabled={!selectedDate || !selectedTime}
            onClick={() => setStep("details")}
            className="pr-cta"
            icon={<ArrowRightOutlined />}
          >
            Continue
          </Button>
        </div>
      )}

      {/* ── Step 2: Guest Details ── */}
      {step === "details" && (
        <div className="pr-body">
          {/* Booking summary bar */}
          <div className="pr-summary">
            <div className="pr-summary__item">
              <CalendarOutlined />
              <span>{selectedDate?.format("ddd, DD MMM YYYY")}</span>
            </div>
            <div className="pr-summary__sep" />
            <div className="pr-summary__item">
              <ClockCircleOutlined />
              <span>{selectedTime}</span>
            </div>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark={false}
            className="pr-form"
          >
            <Form.Item
              name="guestName"
              label="Full Name"
              rules={[{ required: true, message: "Please enter your name" }]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="John Smith"
                size="large"
                className="pr-input"
                autoComplete="name"
              />
            </Form.Item>

            <Form.Item
              name="guestPhone"
              label="Phone Number"
              rules={[{ required: true, message: "Please enter your phone number" }]}
            >
              <Input
                prefix={<PhoneOutlined />}
                placeholder="+381 60 123 4567"
                size="large"
                className="pr-input"
                type="tel"
                autoComplete="tel"
                inputMode="tel"
              />
            </Form.Item>

            <Form.Item
              name="guestEmail"
              label="Email (optional)"
              rules={[{ type: "email", message: "Invalid email address" }]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="email@example.com"
                size="large"
                className="pr-input"
                type="email"
                autoComplete="email"
                inputMode="email"
              />
            </Form.Item>

            {/* Touch-friendly guest stepper */}
            <div className="pr-field-block">
              <label className="pr-label" style={{ marginBottom: 10 }}>
                <TeamOutlined className="pr-label__icon" /> Number of Guests
              </label>
              <div className="pr-stepper">
                <button
                  type="button"
                  className="pr-stepper__btn"
                  onClick={() => setGuestCount((n) => Math.max(1, n - 1))}
                  disabled={guestCount <= 1}
                >
                  <MinusOutlined />
                </button>
                <span className="pr-stepper__value">{guestCount}</span>
                <button
                  type="button"
                  className="pr-stepper__btn"
                  onClick={() => setGuestCount((n) => Math.min(30, n + 1))}
                  disabled={guestCount >= 30}
                >
                  <PlusOutlined />
                </button>
              </div>
            </div>

            <Form.Item name="note" label="Special Requests (optional)">
              <Input.TextArea
                rows={3}
                placeholder="Allergies, special occasions, high chair..."
                maxLength={300}
                className="pr-textarea"
              />
            </Form.Item>

            <div className="pr-form-actions">
              <Button
                size="large"
                onClick={() => setStep("datetime")}
                icon={<ArrowLeftOutlined />}
                className="pr-back-btn"
              >
                Back
              </Button>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={submitting}
                className="pr-cta pr-cta--submit"
                icon={<CheckCircleOutlined />}
              >
                Confirm Reservation
              </Button>
            </div>
          </Form>
        </div>
      )}

      {/* ── Step 3: Success ── */}
      {step === "success" && confirmationData && (
        <div className="pr-success">
          <div className="pr-success__icon">
            <CheckCircleOutlined />
          </div>
          <h2 className="pr-success__title">
            {confirmationData.status === "confirmed"
              ? "Reservation Confirmed! 🎉"
              : "Request Received!"}
          </h2>
          <p className="pr-success__msg">{confirmationData.message}</p>

          <div className="pr-success__details">
            <div className="pr-success__detail-row">
              <CalendarOutlined />
              <span>{selectedDate?.format("dddd, DD MMMM YYYY")}</span>
            </div>
            <div className="pr-success__detail-row">
              <ClockCircleOutlined />
              <span>{selectedTime}</span>
            </div>
            <div className="pr-success__detail-row">
              <TeamOutlined />
              <span>{guestCount} {guestCount === 1 ? "guest" : "guests"}</span>
            </div>
          </div>

          <p className="pr-success__contact">
            We look forward to seeing you! If you need to make changes, please call us directly.
          </p>

          <Button
            size="large"
            block
            onClick={handleReset}
            className="pr-new-btn"
          >
            Make Another Reservation
          </Button>
        </div>
      )}

      {/* ── Footer ── */}
      <div className="pr-footer">
        <span>Powered by</span>
        <strong> FixmanHR</strong>
      </div>

      {/* ── Voice booking FAB ── */}
      <VoicePublicReservation clientId={clientId} />
    </div>
  );
};

export default PublicReservation;
